import type { ToolAnswer, ToolContext, ToolDef } from '../mcp-types';
import { ACTION_TABLE, ActionField, ActionRow } from '../actions/action-table';
import { setOrNot } from '../actions/secrets';
import type { ActionBehaviours } from './do-action.tool';
import type { WhereAmIResult } from './where-am-i.tool';

export interface ReadScreenDeps {
  locate: (ctx: ToolContext) => WhereAmIResult;
  /** What the screen on show reports it holds, by field name, or null when it reports nothing. */
  holds: (screen: string) => Record<string, unknown> | null;
  /** True while the screen's data is still arriving. */
  loading?: (screen: string) => boolean;
  /** How long a read waits for that before answering with what it has. */
  loadWaitMs?: number;
  behaviours: ActionBehaviours;
  /** The real table unless a check is moving a row. */
  table?: readonly ActionRow[];
}

const LOAD_WAIT_MS = 4000;

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

function describeAction(row: ActionRow, behaviours: ActionBehaviours) {
  const commit = row.commit;
  return {
    id: row.id,
    label: row.label,
    lane: row.lane,
    fields: row.fields,
    ...(commit.kind === 'own-tool' ? { tool: commit.tool } : {}),
    ...(commit.kind === 'press' ? { builderPresses: commit.button } : {}),
    ...(commit.kind === 'overlay' ? { builderConfirms: true } : {}),
    ...(commit.kind !== 'own-tool' && !behaviours[row.id]?.[row.lane] ? { notBuilt: true, ...(row.whyNotBuilt ? { whyNotBuilt: row.whyNotBuilt } : {}) } : {}),
  };
}

export function makeReadScreenTool(deps: ReadScreenDeps): ToolDef {
  const table = deps.table ?? ACTION_TABLE;

  return {
    name: 'read_screen',
    kind: 'read',
    worksWithoutProject: true,
    description: [
      'Reads the builder\'s screen: where it is, what it holds, and the actions available there, each with its lane.',
      'A fast-lane action runs when you call do_action. A confirm-lane action is prepared, and the builder clicks in Zavo.',
      'notBuilt means do_action can\'t do that one yet. A secret reads only as "set" or "not set", never its value.',
      'notRead names fields the screen didn\'t report, which says nothing about whether they are empty.',
      'stillLoading means the screen hadn\'t finished loading: read it again before saying anything is missing.',
      'It changes nothing and needs no input.',
    ].join(' '),
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },

    async run(_input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const until = Date.now() + (deps.loadWaitMs ?? LOAD_WAIT_MS);
      while (deps.loading?.(deps.locate(ctx).screen) && Date.now() < until) await new Promise((r) => setTimeout(r, 100));
      const place = deps.locate(ctx);
      const stillLoading = !!deps.loading?.(place.screen);
      const rows = table.filter((row) => row.screen === place.screen || row.screen === 'everywhere');
      const reported = deps.holds(place.screen) ?? {};

      const fields = new Map<string, ActionField>();
      for (const row of rows) {
        if (row.screen === place.screen) for (const f of row.fields) fields.set(f.name, f);
      }

      const holds: Record<string, unknown> = {};
      const notRead: string[] = [];
      for (const [name, field] of fields) {
        if (field.picks) continue;
        // While loading, a blank is only a value that hasn't arrived, so it must not read as "not set" or as empty.
        if (!(name in reported) || (stillLoading && (field.secret || isBlank(reported[name])))) notRead.push(name);
        else holds[name] = field.secret ? setOrNot(reported[name]) : reported[name];
      }

      return {
        outcome: 'done',
        changed: [],
        result: {
          ...place,
          holds,
          ...(notRead.length ? { notRead } : {}),
          ...(stillLoading ? { stillLoading } : {}),
          actions: rows.map((row) => describeAction(row, deps.behaviours)),
        },
      };
    },
  };
}
